// @ts-nocheck
/* eslint-disable camelcase */
/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable no-underscore-dangle */
import React, {
  useEffect, useState, useRef, useMemo,
} from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DialogContentText, Typography, Alert,
} from '@mui/material';
import _, { cloneDeep, unset } from 'lodash';
import { Record as CoreRecord } from '@fulcrumapp/fulcrum-core';
import type { Record, RecordTerms } from '../editor/record.types';
import { CustomConfirmDialog } from '../shared/material_components';
import CustomDrawer from '../shared/material_components/custom-drawer';
import EditorFields from '../editor/fields/editor-fields';
import { useApp } from '../hooks/use-app';
import ValidateFormFields, {
  ValidationErrorFields,
} from '../utils/validate-form-fields';

import type { SystemAppData } from '../hooks/use-system-app';
import editorStack from '../editor/editor-stack';
import type { LinkedRecord } from '../shared/material_components/linked-cell';
import type { DataViewApp } from '../hooks/use-form-app';
import { FormAppContext, initializeForm, prepareFromJSON } from '../hooks/use-form-app';
import LegacyRecordEditor from '../editor/editor-modal';
import appLinkSchema from '../tasks/fields/app-link-field-schema';
import type { Instruction } from '../hooks/use-form-fields';
import ToastAction from '../components/info-elements/toast-action';
import type { Option, Action } from './task-recurrence-dialog';
import TaskRecurrenceDialog from './task-recurrence-dialog';
import * as EDITOR from './record-editor-helper';

export interface EditorState {
  record: Record<any>,
  formData?: SystemAppData,
  shouldLinkToParent: boolean,
  parent?: Record<any>,
}

export interface ParentRecordState {
  currentParentRecord?: LinkedRecord,
  newParentRecord?: LinkedRecord,
  allowMultiple?: boolean,
}

interface Props {
  title?: string;
  creating?: boolean;
  row: Record<object>;
  formData?: SystemAppData | null;
  handleViewRecord?: (record: Record<any> | null) => void;
  handleCloseModal: (record: Record<any> | null) => void;
  fields?: Array<any>;
  openRecordInEditor: (
    parentRecord?: Record<any>,
    record?: Record<any>,
    formData?: SystemAppData,
    shouldLinkToParent?: boolean
  ) => void;
  handleUpdateInitialState?: (record: Record<any>) => void;
  terminology?: RecordTerms,
  useLegacyEditor?: boolean,
}

const RecordEditor: React.FC<Props> = ({
  title = 'task', creating = false, row, formData, handleCloseModal, fields = [],
  openRecordInEditor, handleUpdateInitialState, terminology, useLegacyEditor,
  handleViewRecord,
}: Props) => {
  const alertRef = useRef<null | HTMLDivElement>(null);
  const app = useApp();
  const [error, setError] = useState('');
  const [errorsFields, setErrorsFields] = useState(['']);
  const [currentRecord, setCurrentRecord] = useState(cloneDeep(row));
  const [currentForm, setCurrentForm] = useState(formData);
  const [parentRecordState, setParentRecordState] = useState<ParentRecordState>(
    {} as ParentRecordState,
  );
  const [formErrors, setFormErrors] = useState(
    ValidateFormFields(fields, currentRecord),
  );
  const [allowCreateRecord, setAllowCreateRecord] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [taskRecurrenceOptions, setTaskRecurrenceOptions] = useState(false);

  const [openLegacyEditor, setOpenLegacyEditor] = useState(useLegacyEditor);
  const [isCreatingLegacyEditor, setIsCreatingLegacyEditor] = useState(false);
  const [formApp, setFormApp] = useState<DataViewApp>(null);
  const [legacyRecord, setLegacyRecord] = useState<CoreRecord>(useLegacyEditor ? row : null);
  const [specialInstructions, setSpecialInstructions] = useState<Instruction[]>(null);

  const isBasicTask = formData?.restForm?.system_type === 'basic_task';
  const initRecordSeries = useMemo(() => (
    currentRecord.record_series_id ? currentRecord : null), []);
  const updateRecordSeries = EDITOR.updateRecordSchedule(initRecordSeries);

  const action: Action = {
    changeType: updateRecordSeries.templateFieldChanges(currentRecord),
    rruleUpdate: updateRecordSeries.rruleHasChanged(currentRecord),
  };

  useEffect(() => {
    if (openLegacyEditor) {
      (async () => {
        if (!currentForm?.restForm) {
          return null;
        }
        if (!formApp) {
          const prepareResult = await prepareFromJSON(currentForm.restForm, app.dataSource);
          const dataViewApp = await initializeForm(cloneDeep(app), currentForm?.restForm.id, prepareResult);
          setFormApp(cloneDeep(dataViewApp));
        }
      })();
    }
  }, [app, currentForm, legacyRecord]);

  useEffect(() => {
    setFormErrors(ValidateFormFields(fields, currentRecord));
  }, [fields]);

  useEffect(() => {
    if (row) {
      const formattedRow = EDITOR.formatSeries(isBasicTask, row);
      setCurrentRecord(cloneDeep(formattedRow));
    }
  }, [row, formData]);

  const mutateRecord = creating && allowCreateRecord
    ? async (newRecord: Record<object>) => app?.api?.records?.create(newRecord)
    : async (record: Record<object>) => app?.api?.records?.update(record.id, record, null);

  const queryClient = useQueryClient();

  const saveRecord = useMutation({
    mutationFn: mutateRecord,
    onError: () => {
      setError(
        creating
          ? `Couldn't create the ${title}`
          : `Couldn't modify the selected ${title}`,
      );
      alertRef.current?.scrollIntoView({ behavior: 'smooth' });
    },
  });

  const handleClose = (createdRecord: any) => {
    setCurrentRecord(cloneDeep(row));
    handleCloseModal(createdRecord);
  };

  const handleSubmit = async () => {
    if (action.changeType && !action.rruleUpdate) {
      setTaskRecurrenceOptions(true);
    } else {
      await save();
    }
  };

  const save = async (option?: Option) => {
    let seriesSanitizedRecord = { ...currentRecord };

    if (isBasicTask) {
      seriesSanitizedRecord = updateRecordSeries.handleRRule({
        option,
        setTaskRecurrenceOptions,
        currentRecord,
      });
    }

    if (!ValidateFormFields(fields, seriesSanitizedRecord).length) {
      const currentRecordCopy = cloneDeep(seriesSanitizedRecord);
      unset(currentRecordCopy?.form_values, 'issue_link');
      const cleanCurrentRecord = EDITOR.cancelRecordSeries(currentRecordCopy);

      const createdRecord = await toast.promise(saveRecord.mutateAsync(cleanCurrentRecord), {
        pending: `Saving ${title}`,
        success: {
          icon: false,
          render({ data }) {
            return (
              <ToastAction
                message={`The ${title} has been ${
                  creating ? 'created' : 'updated'
                } successfully`}
                callback={() => handleViewRecord(data)}
                text={`View ${title}`}
              />
            );
          },
        },
        error: `There was an error saving the ${title}`,
      });

      await EDITOR.handleParentRecordChanges(
        createdRecord, cleanCurrentRecord, parentRecordState,
        setAllowCreateRecord, saveRecord.mutateAsync, queryClient,
      );

      // EDITOR.handleParentRecordChanges will mutate parent records (not the currentRecord)
      // invalidate the queries afer all mutations are complete instead of in the mutation handler
      // Must invalidate all queries for the first record in a table case
      queryClient.invalidateQueries();
      setErrorsFields([]);
      // EDITOR.handleParentRecordChanges will set allowCreateRecord to false. Clean it up here.
      setAllowCreateRecord(true);
      handleClose(createdRecord);
    } else {
      setErrorsFields(ValidationErrorFields(formErrors));
    }
  };

  const linkSelectedRecords = (
    key: string,
    taskIDs: any[],
    currentParentRecordState: ParentRecordState,
  ) => {
    const newLinkedRecords: any[] = [];
    let newCurrentRecord: Record<any> = cloneDeep(currentRecord) || {};
    if (key === 'issue_link') {
      taskIDs.forEach((linkedRecord: any) => newLinkedRecords.push(linkedRecord));
      newCurrentRecord = {
        ...currentRecord,
        form_values: {
          ...currentRecord?.form_values,
          [key]: newLinkedRecords,
        },
      };
      if (!_.isEmpty(currentParentRecordState)) {
        setParentRecordState(currentParentRecordState);
      }
    } else {
      taskIDs.forEach((linkedRecord: any) => newLinkedRecords
        .push({ record_id: linkedRecord.recordId }));
      newCurrentRecord = {
        ...currentRecord,
        form_values: {
          ...currentRecord?.form_values,
          [key]: newLinkedRecords,
        },
      };
    }
    setCurrentRecord(cloneDeep(newCurrentRecord));
    if (handleUpdateInitialState) {
      handleUpdateInitialState(newCurrentRecord);
    }
  };

  const linkCreatedLegacyRecords = (record: Record<any>) => {
    if (useLegacyEditor) {
      return;
    }
    const currentField = currentRecord?.form_values?.x758 || [];
    const newLinkedRecords = currentField[0]?.values?.ccc2 || [];
    const isAlreadyLinked = newLinkedRecords.some((field) => record.id === field.record_id);
    if (!isAlreadyLinked) {
      newLinkedRecords.push({
        record_id: record.id,
      });
    }
    const rebuiltFields = currentField[0].elements.map((element) => {
      if (element.type === appLinkSchema.ChoiceField.type) {
        return {
          ...element,
          ...appLinkSchema.ChoiceField,
          choices: [{ label: currentForm?.restForm?.name, value: currentForm?.restForm?.id }],
        };
      }
      return {
        ...element,
        ...appLinkSchema.RecordLinkField,
        form_id: currentForm?.restForm?.id,
      };
    });
    const newCurrentRecord = {
      ...currentRecord,
      form_values: {
        ...currentRecord.form_values,
        x758: [{
          ...currentField[0],
          elements: rebuiltFields,
          values: {
            ...currentField[0].values,
            ccc2: newLinkedRecords || [],
          },
        }],
      },
    };

    setCurrentRecord(cloneDeep(newCurrentRecord));
    if (handleUpdateInitialState) {
      handleUpdateInitialState(cloneDeep(newCurrentRecord));
    }
  };

  const handleOpenRecord = async (
    record?: Record<any>,
    nextFormData?: SystemAppData,
    shouldLinkToParent = false,
    isAppLink = false,
    instructions?: Instruction[],
  ) => {
    if (isAppLink) {
      if (nextFormData) {
        const dataViewApp = await initializeForm(cloneDeep(app), nextFormData.restForm.id);
        const appRecord = new CoreRecord(record, dataViewApp.form);
        const addMode = !appRecord.createdAt;
        setOpenLegacyEditor(true);
        setIsCreatingLegacyEditor(addMode);
        setLegacyRecord(appRecord);
        setCurrentForm(nextFormData);
        if (instructions) {
          setSpecialInstructions(instructions);
        }
      }
    } else {
      openRecordInEditor(currentRecord, record, nextFormData, shouldLinkToParent);
    }
  };
  const editorCopy: EDITOR.SystemTypeCopyTypes = EDITOR.getRecordEditorCopy(title);

  const drawerTitle = creating ? editorCopy.create : `${editorCopy.edit} ${currentRecord?.record_key}`;
  const positiveButtonCopy = saveRecord.isLoading ? editorCopy.mutate : editorCopy.positive;

  const handleValidateChanges = () => {
    if (hasChanges) {
      setShowConfirmDialog(true);
    } else {
      handleCloseModal(null);
    }
  };

  return (
    <>
      {!openLegacyEditor && (
        <CustomDrawer
          open
          title={drawerTitle}
          onPositive={handleSubmit}
          onNegative={handleValidateChanges}
          onClose={handleValidateChanges}
          positive={positiveButtonCopy}
          negative={editorCopy.negative}
          positiveDisabled={saveRecord.isLoading}
          negativeDisabled={saveRecord.isLoading}
          serverError={!!error}
        >
          {error ? (<Alert severity="error">{error}</Alert>) : null}
          <EditorFields
            form={formData}
            record={currentRecord}
            fields={fields}
            errorsFields={errorsFields}
            onChange={(record: Record<object>) => {
              setHasChanges(true);
              setCurrentRecord(record);
              setFormErrors(ValidateFormFields(fields, currentRecord));
            }}
            openRecordInEditor={handleOpenRecord}
            linkSelectedRecords={linkSelectedRecords}
            terminology={terminology}
            handleClose={() => handleCloseModal(null)}
          />
        </CustomDrawer>
      )}
      {openLegacyEditor && formApp && (
        <FormAppContext.Provider value={formApp}>
          <LegacyRecordEditor
            record={legacyRecord}
            feature={legacyRecord}
            show={openLegacyEditor}
            previewMode={false}
            viewOnlyMode={false}
            addMode={isCreatingLegacyEditor}
            editing={!isCreatingLegacyEditor}
            onSave={linkCreatedLegacyRecords}
            topBorder
            onClose={() => {
              if (useLegacyEditor) {
                handleCloseModal(null);
                return;
              }
              setLegacyRecord(null);
              setOpenLegacyEditor(false);
            }}
            specialInstructions={specialInstructions}
          />
        </FormAppContext.Provider>
      )}
      <TaskRecurrenceDialog
        action={action.changeType}
        open={taskRecurrenceOptions}
        onPositive={(option: Option) => save(option)}
        onNegative={() => setTaskRecurrenceOptions(false)}
      />
      <CustomConfirmDialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        title="Discard Changes"
        positive="Discard Changes"
        onNegative={() => setShowConfirmDialog(false)}
        onPositive={() => {
          setShowConfirmDialog(false);
          setHasChanges(false);
          handleCloseModal(null);
        }}
      >
        <DialogContentText component="span" data-testid="dialog-content">
          <Typography variant="body1">
            This
            {' '}
            {title}
            {' '}
            has unsaved changes. If you discard now, all the information will be lost.
          </Typography>
        </DialogContentText>
      </CustomConfirmDialog>
    </>
  );
};

export default editorStack(RecordEditor);
